'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import { type ReactNode, useState } from 'react';
import { useCompetitionLabels } from '@/components/social/competition/competition-labels';
import { competitionRankingMetric, type TradingCompetitionSummary } from '@/components/social/competition/types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/app-tabs';
import { Calendar03Icon, ChampionIconSolid, DollarCircleIcon, UserMultiple02IconSolid } from '@/components/ui/icon/glyphs';
import { InfoModal } from '@/components/ui/info-modal';
import { MotionAnimatedAutoHeight } from '@/components/ui/motion-animated-auto-height';
import { formatCompetitionWindow } from '@/lib/competitions/format';
import { useFormattedCurrency } from '@/lib/currency';
interface CompetitionAboutSheetProps {
  competition: TradingCompetitionSummary;
  timeZone: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialView?: 'overview' | 'rules';
}
const CASH_PROFIT_EXAMPLE = {
  cashInD6: '192638820000',
  cashOutD6: '168924570000',
  pumpProfitD6: '26638690000',
  cashProfitD6: '-2924440000'
} as const;
const CASH_PROFIT_PUMP_THRESHOLD_D6 = '1000000000';
const CASH_PROFIT_DAILY_BUDGET_D6 = '1000000000';
const CASH_PROFIT_SEVEN_DAY_BUDGET_D6 = '7000000000';

// The only place the rules, exact times and prize are written out in full; the
// hero and standings never repeat them.
export function CompetitionAboutSheet(t0) {
  const $ = _c(67);
  const {
    competition,
    timeZone,
    open,
    onOpenChange,
    initialView: t1
  } = t0;
  const initialView = t1 === undefined ? "overview" : t1;
  const t = useTranslations("competitions.about");
  const rules = useTranslations("competitions.rules");
  const locale = useLocale();
  const labels = useCompetitionLabels();
  const {
    formatD6
  } = useFormattedCurrency();
  const explainsCashProfit = competition.rankingSource === "cash_profit";
  const [activeView, setActiveView] = useState(initialView);
  let t2;
  if ($[0] !== t) {
    t2 = t("rankedBy");
    $[0] = t;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== competition.board || $[3] !== labels) {
    t3 = labels.board(competition.board);
    $[2] = competition.board;
    $[3] = labels;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== competition || $[6] !== labels) {
    t4 = labels.metric(competitionRankingMetric(competition));
    $[5] = competition;
    $[6] = labels;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = `${t3} · ${t4}`;
  let t6;
  if ($[8] !== t2 || $[9] !== t5) {
    t6 = <InfoModal.IconItem icon={ChampionIconSolid} title={t2} description={t5} />;
    $[8] = t2;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t) {
    t7 = t("window");
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== competition || $[14] !== locale || $[15] !== timeZone) {
    t8 = formatCompetitionWindow(competition, locale, timeZone);
    $[13] = competition;
    $[14] = locale;
    $[15] = timeZone;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <InfoModal.IconItem icon={Calendar03Icon} title={t7} description={t8} />;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t) {
    t10 = t("prizePool");
    $[20] = t;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== competition.prizePoolD6 || $[23] !== formatD6) {
    t11 = formatD6(competition.prizePoolD6, "full");
    $[22] = competition.prizePoolD6;
    $[23] = formatD6;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11) {
    t12 = <InfoModal.IconItem icon={DollarCircleIcon} title={t10} description={t11} />;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t) {
    t13 = t("entry");
    $[28] = t;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t) {
    t14 = t("entryAutomatic");
    $[30] = t;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t13 || $[33] !== t14) {
    t15 = <InfoModal.IconItem icon={UserMultiple02IconSolid} title={t13} description={t14} />;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t12 || $[36] !== t15 || $[37] !== t6 || $[38] !== t9) {
    t16 = <InfoModal.IconList>{t6}{t9}{t12}{t15}</InfoModal.IconList>;
    $[35] = t12;
    $[36] = t15;
    $[37] = t6;
    $[38] = t9;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== competition.rankingSource || $[41] !== t) {
    t17 = competition.rankingSource === "board" ? t("noteBoard") : t("noteAdjusted");
    $[40] = competition.rankingSource;
    $[41] = t;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t17) {
    t18 = <p className="text-caption text-foreground-tertiary">{t17}</p>;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t16 || $[46] !== t18) {
    t19 = <>{t16}{t18}</>;
    $[45] = t16;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  const overview = t19;
  let t20;
  if ($[48] !== initialView || $[49] !== onOpenChange) {
    t20 = nextOpen => {
      if (!nextOpen) {
        setActiveView(initialView);
      }
      onOpenChange(nextOpen);
    };
    $[48] = initialView;
    $[49] = onOpenChange;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== t) {
    t21 = t("title");
    $[51] = t;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  const t22 = explainsCashProfit ? "lg" : "default";
  let t23;
  if ($[53] !== activeView || $[54] !== explainsCashProfit || $[55] !== formatD6 || $[56] !== overview || $[57] !== rules || $[58] !== t) {
    t23 = explainsCashProfit ? <Tabs value={activeView} onValueChange={setActiveView}><TabsList variant="segmented" size="md"><TabsTrigger value="overview">{t("overviewTab")}</TabsTrigger><TabsTrigger value="rules">{rules("title")}</TabsTrigger><TabsTrigger value="calculation">{t("calculationTab")}</TabsTrigger></TabsList><MotionAnimatedAutoHeight layoutDependencies={[activeView]}><TabsContent value="overview" className="flex flex-col gap-4 pt-4">{overview}</TabsContent><TabsContent value="rules" className="pt-4"><ul className="flex list-disc flex-col gap-3 pl-5 text-body text-foreground-secondary"><li>{rules("window")}</li><li>{rules("score")}</li><li>{rules("wallet")}</li><li>{rules("transfers")}</li><li>{rules("collusion")}</li><li>{rules("ineligible")}</li><li>{rules("team")}</li><li>{rules("prizes")}</li><li>{rules("results")}</li><li>{rules("independent")}</li></ul></TabsContent><TabsContent value="calculation" className="flex flex-col gap-4 pt-4"><div className="flex flex-col gap-1"><h3 className="text-heading-xs text-foreground-primary">{t("cashProfitTitle")}</h3><p className="text-body text-foreground-secondary">{t("cashProfitIntro")}</p></div><InfoModal.Steps><InfoModal.Step title={t("cashProfitReceivedTitle")}>{t("cashProfitReceivedDescription")}</InfoModal.Step><InfoModal.Step title={t("cashProfitSpentTitle")}>{t("cashProfitSpentDescription")}</InfoModal.Step><InfoModal.Step title={t("cashProfitPumpTitle")}>{t("cashProfitPumpDescription", {
                pumpThreshold: formatD6(CASH_PROFIT_PUMP_THRESHOLD_D6, "full")
              })}</InfoModal.Step><InfoModal.Step title={t("cashProfitScalingTitle")}>{t("cashProfitScalingDescription", {
                dailyBudget: formatD6(CASH_PROFIT_DAILY_BUDGET_D6, "full"),
                sevenDayBudget: formatD6(CASH_PROFIT_SEVEN_DAY_BUDGET_D6, "full")
              })}</InfoModal.Step></InfoModal.Steps><InfoModal.Callout>{t("cashProfitExample", {
              cashIn: formatD6(CASH_PROFIT_EXAMPLE.cashInD6, "full"),
              cashOut: formatD6(CASH_PROFIT_EXAMPLE.cashOutD6, "full"),
              pumpProfit: formatD6(CASH_PROFIT_EXAMPLE.pumpProfitD6, "full"),
              cashProfit: formatD6(CASH_PROFIT_EXAMPLE.cashProfitD6, "full")
            })}{" "}{t("cashProfitRealizedDifference")}</InfoModal.Callout></TabsContent></MotionAnimatedAutoHeight></Tabs> : overview;
    $[53] = activeView;
    $[54] = explainsCashProfit;
    $[55] = formatD6;
    $[56] = overview;
    $[57] = rules;
    $[58] = t;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== competition.title || $[61] !== open || $[62] !== t20 || $[63] !== t21 || $[64] !== t22 || $[65] !== t23) {
    t24 = <InfoModal open={open} onOpenChange={t20} title={t21} description={competition.title} size={t22}>{t23}</InfoModal>;
    $[60] = competition.title;
    $[61] = open;
    $[62] = t20;
    $[63] = t21;
    $[64] = t22;
    $[65] = t23;
    $[66] = t24;
  } else {
    t24 = $[66];
  }
  return t24;
}